/**
 * mutationParams.ts - Shared dependency bundle and plumbing for table mutation methods.
 *
 * Each mutation method (see the sibling files in this directory) is a plain function whose first
 * argument is a {@link TableMutationParams} bundle, followed by its own call arguments. The single
 * `useTableMutations` hook builds `params` once and binds it into each method. Keeping these as plain
 * functions (rather than hooks) makes them unit-testable with a fake `params` and lets dependent
 * methods call their siblings directly.
 */

import DocumentStore from 'notebook/documents/DocumentStore';
import type {Table} from 'notebook/tables/constants/TableConstants';
import type {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {Selector} from 'notebook/tables/excess/EvaluationConstants';
import type {TableOpDiff, TableOpDiffMap} from 'notebook/tables/excess/ops/TableOpConstants';
import type {useTableOps} from 'notebook/tables/spreadsheets/TableOpContext';
import type {TableNoteContextInterface} from 'notebook/tables/store/contexts/TableNoteContext';
import type {TableModel} from 'notebook/tables/store/models/TableModel';
import type {WorksheetInfo} from 'notebook/tables/util/analytics/CrossStepReferenceAnalytics';
import {getWorksheetIdFromNamespace} from 'notebook/tables/util/WorksheetTableNamespaceUtil';
import type {Optional} from 'util/Types';

/**
 * Dependencies threaded into every mutation method. Built once by `useTableMutations` and passed as
 * the first argument to each method. All model data is read fresh via {@link getTableModel} at call
 * time, so the bundle itself stays stable across renders.
 */
export interface TableMutationParams {
  /** Reads the latest {@link TableModel} from the store. Always call this at the top of a method. */
  getTableModel: () => TableModel;
  /** Writes partial editable state to the store (selection, dragging, etc.). */
  setState: (state: Partial<TableEditableState>) => void;
  /**
   * The note context for the mounted surface (entry/worksheet/template), or `null` in isolated
   * contexts (tests/harnesses). Used by merge validation to scan the whole note for references.
   */
  tableNoteContext: TableNoteContextInterface | null;
  /** The underlying table ops (reorder, insert/delete rows/columns, merge, etc.). */
  tableOps: ReturnType<typeof useTableOps>;
}

/**
 * Persist a table mutation result via the model's `updateHandler`, wrapping the tableOp in the
 * `{[tableName]: tableOp}` map shape the handler expects (omitted when there is no op).
 */
export function persistTable(
  updateHandler: (
    index: number,
    payload: {table: Table},
    selectors?: Optional<Array<Selector>>,
    tableOp?: TableOpDiffMap
  ) => void,
  index: number,
  tableName: string,
  result: {table: Table; tableOp: TableOpDiff | null},
  selectors?: Optional<Array<Selector>>
): void {
  updateHandler(
    index,
    {table: result.table},
    selectors,
    result.tableOp != null ? {[tableName]: result.tableOp} : undefined
  );
}

/**
 * Compute the {@link WorksheetInfo} for cross-step reference analytics, or `null` when the table's
 * namespace does not correspond to a worksheet.
 */
export function computeWorksheetInfo(tableNamespace: string, fileId: string): WorksheetInfo | null {
  const worksheetId = getWorksheetIdFromNamespace(tableNamespace);
  return worksheetId ? {worksheetId, stepCount: DocumentStore.getRelatedDocumentIds(fileId).length} : null;
}
